import { useMemo, useState } from 'react'
import Icon from './Icon.jsx'
import { formatShort, weekdayName, windowLabel } from '../lib/dates.js'
import { useEscapeKey } from '../lib/useEscapeKey.js'
import { conflictsOn } from '../lib/conflicts.js'

const NOT_YOURS = 'Only someone who can fill this board can change it'

/**
 * Everything on this board that contradicts itself, in one column.
 *
 * A conflict is only visible on the grid as a booked cell on a hatched row — findable if you
 * happen to look at that day, and invisible on every other view. "What have we got wrong
 * this fortnight" is a question about the whole range at once, so it gets a panel, the same
 * way the internal notes do.
 *
 * Scope is exactly what is on screen, read out of the loaded board: the days in view, the
 * mode being shown, the people this actor may see. See lib/conflicts.js for the rule that
 * decides what counts, and why a part-day absence is offered separately rather than counted.
 *
 * Every row offers the three ways a conflict actually ends, because there are only three:
 *   change the absence — the row opens the time-off dialog, which is also where the rest of
 *     what it runs into is listed;
 *   remove the booking — the other half of the contradiction, undoable like any other
 *     removal on this page;
 *   mark it as resolved — say it is deliberate. It leaves the list for everybody, not just
 *     for you, because the next person to open this panel should not have to decide it again.
 *
 * Deliberately NOT "show me on the board". A whole-day absence replaces that person's row
 * with a single hatched cell, so the booking underneath it is drawn on no view at all — the
 * click would land on nothing, which is the very reason these are worth a panel of their own.
 */
export default function ConflictsDrawer({
  board,
  staffById,
  canResolve,
  onClose,
  onEditTimeOff,
  onRemoveBooking,
  onResolve,
  onUnresolve,
}) {
  const [busyKey, setBusyKey] = useState(null)
  useEscapeKey(onClose)

  const all = useMemo(() => conflictsOn(board), [board])
  const open = all.filter((c) => c.kind === 'conflict' && !c.resolved)
  const possible = all.filter((c) => c.kind === 'possible' && !c.resolved)
  const settled = all.filter((c) => c.resolved)

  const byDate = (list) => {
    const m = new Map()
    for (const c of list) {
      if (!m.has(c.date)) m.set(c.date, [])
      m.get(c.date).push(c)
    }
    return [...m.entries()]
  }

  async function run(c, fn) {
    setBusyKey(c.key)
    try {
      await fn()
    } finally {
      setBusyKey(null)
    }
  }

  const Card = ({ c }) => {
    const name = staffById?.get(c.assignment.staffId)?.name || 'Someone'
    const hours = windowLabel(c.timeOff, c.date)
    return (
      <article className={`conflict-card ${c.kind}`}>
        <button
          type="button"
          className="conflict-card-hit"
          title="Open this time off to change it"
          onClick={() => onEditTimeOff?.(c)}
        >
          <div className="conflict-card-head">
            <strong>{name}</strong>
            <span className="conflict-card-job">{c.assignment.label}</span>
          </div>
          {/* The two halves of the contradiction, one under the other, in the order they get
              said out loud: booked on this, away for that. */}
          <p className="conflict-card-why">
            <Icon name="badgeAlert" size={13} strokeWidth={2} />
            {hours ? `Off ${hours}` : 'Off all day'}
            {c.timeOff.note ? ` · ${c.timeOff.note}` : ''}
          </p>
        </button>

        <div className="conflict-card-actions">
          {c.resolved ? (
            <>
              <span className="conflict-card-settled">
                <Icon name="checkCircle" size={13} strokeWidth={2} />
                Marked as resolved
                {c.resolved.resolvedByName ? ` by ${c.resolved.resolvedByName}` : ''}
              </span>
              {/* A decision, not a fact, so it stays changeable. Somebody who settled the
                  wrong pair has otherwise no way back once the toast has gone. */}
              <button
                type="button"
                className="btn ghost sm"
                disabled={!canResolve || busyKey === c.key}
                title={canResolve ? 'Put this back on the list' : NOT_YOURS}
                onClick={() => run(c, () => onUnresolve(c))}
              >
                <Icon name="back" size={13} strokeWidth={2} />
                {busyKey === c.key ? 'Saving…' : 'Put back'}
              </button>
            </>
          ) : (
            <>
              {/* Removing is the destructive half and wears the destructive colour, which is
                  the one convention worth borrowing here: the two actions sit side by side
                  and differ only in words otherwise. */}
              <button
                type="button"
                className="btn ghost danger sm"
                disabled={!canResolve || busyKey === c.key}
                title={canResolve ? 'Delete the job, not the time off' : NOT_YOURS}
                onClick={() => run(c, () => onRemoveBooking(c.assignment))}
              >
                <Icon name="trash" size={13} strokeWidth={2} />
                Remove job
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={!canResolve || busyKey === c.key}
                title={canResolve ? 'Leave both as they are and stop counting this' : NOT_YOURS}
                onClick={() => run(c, () => onResolve(c))}
              >
                <Icon name="checkCircle" size={13} strokeWidth={2} />
                {busyKey === c.key ? 'Saving…' : 'Mark as resolved'}
              </button>
            </>
          )}
        </div>
      </article>
    )
  }

  const Section = ({ list, children }) =>
    byDate(list).map(([date, rows]) => (
      <section key={date} className="drawer-day">
        <h4 className="drawer-day-head">
          {weekdayName(date)} {formatShort(date)}
        </h4>
        {rows.map((c) => children(c))}
      </section>
    ))

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Conflicts"
      >
        <div className="drawer-head">
          <div className="drawer-title">
            <strong>Conflicts</strong>
            <span>
              {open.length === 0
                ? 'Nothing to sort out in this range'
                : `${open.length} to sort out · ${formatShort(board.from)} – ${formatShort(board.to)}`}
            </span>
          </div>
          <button type="button" className="cell-pop-x" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="drawer-body">
          {all.length === 0 && (
            <div className="drawer-empty">
              <Icon name="checkCircle" size={22} strokeWidth={1.8} />
              <p>No conflict detected</p>
              <span>Nobody is booked on a day they are away</span>
            </div>
          )}

          <Section list={open}>{(c) => <Card key={c.key} c={c} />}</Section>

          {/* Below the real ones and under their own heading, because they are usually FINE:
              a job on a day somebody is out for the morning is what booking around an
              appointment looks like. Here to be checked, never counted. */}
          {possible.length > 0 && (
            <>
              <h3 className="drawer-section">
                Potential scheduling conflicts
                <span>Booked on a day somebody is away for part of it</span>
              </h3>
              <Section list={possible}>{(c) => <Card key={c.key} c={c} />}</Section>
            </>
          )}

          {settled.length > 0 && (
            <>
              <h3 className="drawer-section">
                Already settled
                <span>Marked as resolved</span>
              </h3>
              <Section list={settled}>{(c) => <Card key={c.key} c={c} />}</Section>
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
